import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Loader2, UserRound, MailCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/site/PasswordInput";
import { API_BASE } from "@/lib/api";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function MemberLogin() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isTrial = params.get("trial") === "1";
  const redirect = params.get("redirect") || "/dashboard";
  const [mode, setMode] = useState(params.get("mode") === "signup" ? "signup" : "login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState(params.get("email") || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return;
    axios.get(`${API_BASE}/member/auth/me`, { withCredentials: true })
      .then(() => navigate(redirect, { replace: true }))
      .catch(() => {});
  }, [navigate, redirect]);

  const googleLogin = () => {
    const redirectUrl = window.location.origin + redirect;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "signup" && !name.trim()) return toast.error("Please enter your name.");
    if (!/^\S+@\S+\.\S+$/.test(email)) return toast.error("Please enter a valid email.");
    if (password.length < 6) return toast.error("Password must be at least 6 characters.");
    setLoading(true);
    try {
      const url = mode === "signup" ? "/member/auth/register" : "/member/auth/login";
      const body = mode === "signup" ? { name, email, password } : { email, password };
      const r = await axios.post(`${API_BASE}${url}`, body, { withCredentials: true });
      navigate(redirect, { state: { user: r.data } });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong.");
      if (typeof detail === "string" && detail.startsWith("No account found")) setMode("signup");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0] px-6 py-10 flex flex-col">
      <div className="max-w-md mx-auto w-full">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#5C665A] hover:text-[#2D372B] mb-8">
          <ArrowLeft size={16} /> Back to site
        </Link>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-full bg-[#89937C]/15 flex items-center justify-center">
            <UserRound size={18} className="text-[#89937C]" />
          </div>
          <h1 className="font-serif-display text-4xl text-[#2D372B]">Member Login</h1>
        </div>
        <p className="text-[#5C665A] mb-8">Your orders, addresses and coffee — all in one place.</p>

        {isTrial && (
          <div data-testid="trial-check-email-banner" className="mb-6 rounded-2xl border-2 border-[#C57B57] bg-[#C57B57]/10 p-5 animate-pulse">
            <div className="flex items-start gap-3">
              <MailCheck size={20} className="text-[#AD6A48] mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold text-[#2D372B]">Step 1 — Check your email!</div>
                <p className="text-sm text-[#5C665A] mt-1">
                  Your TRIAL JAY 7 activation email has been sent{email ? <> to <span className="font-medium text-[#2D372B]">{email}</span></> : ""}.
                  Now <span className="font-semibold text-[#AD6A48]">create your account below</span> — with a password of your choice
                  (works with Yahoo, Outlook, any email) <span className="font-semibold">or</span> Continue with Google — using the <u>same email</u>, then complete your assessment.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white border border-[#E5E2D9] rounded-3xl p-8">
          <button
            data-testid="google-login-btn"
            onClick={googleLogin}
            className="w-full inline-flex items-center justify-center gap-3 rounded-full px-6 py-3.5 border border-[#E5E2D9] text-[#2D372B] font-medium hover:border-[#89937C] hover:bg-[#89937C]/5 transition-colors"
          >
            <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z"/></svg>
            Continue with Google
          </button>
          <div className="flex items-center gap-3 my-6">
            <div className="h-px bg-[#E5E2D9] flex-1" />
            <span className="text-xs text-[#5C665A]">or with email</span>
            <div className="h-px bg-[#E5E2D9] flex-1" />
          </div>
          <form onSubmit={submit} className="space-y-3">
            {mode === "signup" && (
              <Input data-testid="member-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="bg-[#F7F5F0] border-[#E5E2D9] h-12" />
            )}
            <Input data-testid="member-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="bg-[#F7F5F0] border-[#E5E2D9] h-12" />
            <PasswordInput data-testid="member-password-input" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password (min 6 characters)" className="bg-[#F7F5F0] border-[#E5E2D9] h-12" />
            {mode === "login" && (
              <div className="text-right">
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs font-medium text-[#AD6A48] underline underline-offset-2 hover:text-[#2D372B] transition-colors">
                  Forgot password?
                </Link>
              </div>
            )}
            <button
              data-testid="member-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 bg-[#2D372B] text-[#F7F5F0] font-medium hover:bg-[#89937C] transition-colors disabled:opacity-60"
            >
              {loading && <Loader2 size={14} className="animate-spin" />}
              {mode === "signup" ? "Create my account" : "Sign in"}
            </button>
          </form>
          <button
            data-testid="member-mode-toggle"
            onClick={() => setMode(mode === "signup" ? "login" : "signup")}
            className="mt-4 w-full text-center text-sm font-semibold text-[#AD6A48] underline underline-offset-4 hover:text-[#2D372B] transition-colors"
          >
            {mode === "signup" ? "Already a member? Sign in" : "New here? Create an account"}
          </button>
        </div>
      </div>
    </div>
  );
}
